import Spinner from '@strava/ui/Spinner';
import React, { useEffect } from 'react';
import { isEmpty, orderBy } from 'lodash-es';
import useFacebook from 'hooks/externalSocialPartners/useFacebook';
import { AuthorizationStatus } from 'hooks/externalSocialPartners/constants/auth-status-constants';
import StravaAlert from 'components/StravaAlert';
import FacebookFriendsList from './components/FacebookFriendsList';
import LinkFacebookAccount from './components/LinkFacebookAccount';
import styles from './FacebookTabContent.scss';
import { useInviteFriendsContext } from '../../../../state/InviteFriendsContext';
import {
  ADD_ALERT,
  UPDATE_FB_FRIENDS
} from '../../../../state/InviteFriends.actions';
import { t } from '../../../../utils/invite-friends-utils';

const FacebookTabContent = () => {
  const { state, dispatch } = useInviteFriendsContext();
  const { fbAppId, fbVersion, modalTabIndex, alert, locale } = state;

  const { friends, authorizationStatus, requestFriends } = useFacebook({
    appId: fbAppId,
    version: fbVersion,
    locale
  });

  useEffect(() => {
    // When FB tab is selected, call requestFriends as soon as FB script is ready.
    // If user is not already logged in to FB, or if user is logged in to FB but
    // doesn't have the right permissions, auth status will be REQUEST_NEW_PERMISSIONS
    // and we show them the LinkFacebookAccount view
    if (
      modalTabIndex === 1 &&
      authorizationStatus === AuthorizationStatus.READY
    ) {
      requestFriends();
    }
    // requestFriends deliberately left out to prevent multiple calls to this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationStatus, modalTabIndex]);

  // save friends to the shared state
  useEffect(() => {
    if (friends.length > 0) {
      // list friends not yet being followed before friends already being followed
      // and sort alphabetically by name
      const sortedFriends = orderBy(
        friends,
        ['isFollowable', 'displayName'],
        ['desc', 'asc']
      );
      // map friends array to object with the strava ids as the keys
      // save the index of the friend's position in the sorted array
      const fbFriends = Object.fromEntries(
        sortedFriends.map((friend, index) => [
          friend.id,
          { ...friend, sortedIndex: index }
        ])
      );
      dispatch({
        type: UPDATE_FB_FRIENDS,
        payload: { fbFriends }
      });
    }
  }, [dispatch, friends]);

  useEffect(() => {
    if (authorizationStatus === AuthorizationStatus.ERROR) {
      // show error message to athlete when something goes wrong during authorization
      dispatch({
        type: ADD_ALERT,
        payload: {
          alert: { severity: 'error', message: t(`modal.errors.generic`) }
        }
      });
    } else if (authorizationStatus === AuthorizationStatus.UNAUTHORIZED) {
      // show warning message to athlete if they don't authorize FB permissions
      dispatch({
        type: ADD_ALERT,
        payload: {
          alert: {
            severity: 'warning',
            message: t('modal.facebook.unauthorized')
          }
        }
      });
    }
  }, [authorizationStatus, dispatch]);

  return (
    <div className={styles.fbPanel}>
      {!isEmpty(alert) && (
        <StravaAlert severity={alert.severity}>{alert.message}</StravaAlert>
      )}
      {[
        AuthorizationStatus.REQUEST_NEW_PERMISSIONS,
        AuthorizationStatus.ERROR,
        AuthorizationStatus.UNAUTHORIZED
      ].includes(authorizationStatus) && (
        <LinkFacebookAccount
          requestFriends={requestFriends}
          authorizationStatus={authorizationStatus}
        />
      )}
      {authorizationStatus === AuthorizationStatus.DATA_FETCHED && (
        <FacebookFriendsList />
      )}
      {[
        AuthorizationStatus.INITIATED,
        AuthorizationStatus.READY,
        AuthorizationStatus.AUTHORIZED
      ].includes(authorizationStatus) && (
        <div className={styles.centerSpinner}>
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default FacebookTabContent;
